import PageHeader from '../../../components/PageHeader'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import ListHeader from '../../../components/ListHeader'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useBaseService from '../../../hooks/services/useBaseService'
import useNotification from '../../../hooks/useNotification'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import useLoader from '../../../hooks/useLoader'
import { entities } from '../../../constants/entities'
import useAction from '../../../hooks/useAction'
import { useCallback, useEffect } from 'react'
import {
  faEdit,
  faEye,
  faPrint,
  faTags,
  faTrash,
  faUndo,
} from '@fortawesome/free-solid-svg-icons'
import { formatDate } from '../../../utils/formatDate'
import FilterCollapse from '../../../components/FilterCollapse'
import { inputsFiltersEntrance } from './filters/filtres-transactions'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useOrgan } from '../../../hooks/useOrgan'
import { useReport } from '../../../context/ReportContext'

const EntranceTransactionsPage = () => {
  const { canAccess } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()

  const { setExportId } = useReport()

  const { selectedOrgan } = useOrgan()

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      name: [],
      'type.name': [],
      'supplier.name': [],
      'documentType.name': [],
      doc_number: [],
      'user.name': [], // created_by
      'items.product.name': [],
      'items.category.name': [],
      'items.condition.name': [],
      'items.address.alias': [], // Apelido do endereço
      'items.address.street': [], // Rua do endereço
      'items.address.city': [], // Cidade do endereço
      'items.address.state': [], // Estado do endereço
      buyer_id: selectedOrgan?.id,
      deleted_at: false,
    },
    'almox_transactions'
  )

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        name: [],
        'supplier.name': [],
        'documentType.name': [],
        doc_number: [],
        'user.name': [], // created_by
        'items.product.name': [],
        'items.category.name': [],
        'items.condition.name': [],
        'items.address.alias': [], // Apelido do endereço
        'items.address.street': [], // Rua do endereço
        'items.address.city': [], // Cidade do endereço
        'items.address.state': [], // Estado do endereço
        buyer_id: selectedOrgan?.id,
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const {
    data: transactionsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ['almox_transactions', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: {
          ...filtersTransformed,
          transaction_type_id: {
            $eq: 1, // ENTRADA
          },
          supplier_id: {
            $ne: null,
          },
        },
        page: pagination.page,
        perPage: pagination.perPage,
      }
      const response = await baseServiceGet(entities.transactions.get, params)
      return response.result
    },
    refetchOnMount: true,
    select: (data) => {
      return {
        transactions: data?.data?.map((transaction) => ({
          id: transaction.id,
          docNumber: transaction.doc_number ?? '-',
          type: transaction.type?.name || '-',
          docDate: transaction.doc_date
            ? formatDate({ rawDate: transaction.doc_date, showTime: false })
            : '-',
          purchase_date: transaction.purchase_date
            ? formatDate({
                rawDate: transaction.purchase_date,
                showTime: false,
              })
            : '-',
          supplier: transaction.supplier?.alias || '-',
          docType: transaction.documentType?.name || '-',
          organization: transaction.buyer?.name || '-',
          details: [
            {
              purchase_date: transaction.purchase_date
                ? formatDate({
                    rawDate: transaction.purchase_date,
                    showTime: false,
                  })
                : '-',
              docDate: transaction.doc_date
                ? formatDate({ rawDate: transaction.doc_date, showTime: false })
                : '-',
              docType: transaction.documentType?.name || '-',
              amount_total: transaction.amount_total || 0,
              amount_discount: transaction.amount_discount || 0,
              user: transaction.user?.name || '-',
            },
          ],
          user: transaction.user?.name || '-',
          transactionDate: transaction.transaction_date
            ? formatDate({
                rawDate: transaction.transaction_date,
                showTime: false,
              })
            : '-',
          hasEquipments: transaction.has_equipments,
          isDeleted: !!transaction.deleted_at,
        })),
        totalPages: data.last_page,
      }
    },
    placeholderData: (prev) => prev,
  })

  const { transactions = [], totalPages = 1 } = transactionsData || {}

  const columns = [
    { key: 'id', label: 'Número' },
    { key: 'docNumber', label: 'Nº Documento' },
    { key: 'supplier', label: 'Fornecedor' },
    { key: 'organization', label: 'Comprador' },
    { key: 'transactionDate', label: 'Data da Transação' },
  ]

  const collapseConfig = {
    key: ['details'],
    titles: ['Detalhes'],
    columns: [
      [
        { key: 'details.purchase_date', label: 'Data da Compra' },
        { key: 'details.docDate', label: 'Data do Documento' },
        { key: 'details.docType', label: 'Tipo do Documento' },
        { key: 'details.amount_total', label: 'Valor total dos Itens' },
        { key: 'details.amount_discount', label: 'Desconto da Nota' },
        { key: 'details.user', label: 'Criado por' },
      ],
    ],
  }

  useEffect(() => {
    isLoading ? showLoader() : hideLoader()
  }, [isLoading, showLoader, hideLoader])

  useEffect(() => {
    if (isError) {
      console.error('Erro ao carregar produtos:', error)
      showNotification(
        'error',
        `Erro ao carregar produtos: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  const {
    openModalConfirmation,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    action,
  } = useAction(navigate)

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const refreshList = useCallback(() => {
    queryClient.invalidateQueries(['almox_transactions', filters, pagination])
  }, [queryClient, filters, pagination])

  const inactivateTransactionMutation = useMutation({
    mutationFn: async (id) => {
      showLoader()
      const response = await baseServiceDelete(entities.transactions.delete(id))
      return response
    },
    onSuccess: (res) => {
      if (res?.status === 200) refreshList()
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  const activateTransactionMutation = inactivateTransactionMutation

  const exportTags = useMutation({
    mutationFn: async ({ transactionId }) => {
      const params = {}
      const response = await baseServiceGet(
        entities.reports.tagProductFromTransaction(transactionId),
        params
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
    },
  })

  const exportProtocol = useMutation({
    mutationFn: async ({ transactionId, data }) => {
      const params = {}
      const response = await baseServiceGet(
        entities.reports.protocolTransaction(transactionId),
        params,
        {
          ...data,
        }
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
    },
  })

  useEffect(() => {
    if (exportProtocol.isPending) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [exportProtocol.isPending, showLoader, hideLoader])

  const actions = [
    {
      id: 'exportar_etiquetas',
      icon: faTags,
      title: 'Imprimir etiquetas',
      buttonClass: 'btn-success',
      permission: 'Ver transações', // A permissão é a mesma para exportar etiquetas
      onClick: async (transaction) => {
        try {
          await exportTags.mutateAsync({ transactionId: transaction.id })
        } catch (error) {
          console.error('Erro ao exportar etiquetas:', {
            status: error.status,
            message: error.message,
          })
        }
      },
      show: (transaction) =>
        !transaction.isDeleted &&
        !transaction.hasEquipments &&
        canAccess('Ver transações'),
    },
    {
      id: 'exportar_protocolo',
      icon: faPrint,
      title: 'Imprimir Protocolo',
      buttonClass: 'btn-success',
      permission: 'Ver transações', // A permissão é a mesma para exportar protocolo
      onClick: async (transaction) => {
        try {
          const payload = {
            responsible: '',
            cpf_cnpj: '',
          }

          await exportProtocol.mutateAsync({
            transactionId: transaction.id,
            data: payload,
          })
        } catch (error) {
          console.error('Erro ao exportar protocolo:', {
            status: error.status,
            message: error.message,
          })
        }
      },
      show: (transaction) =>
        !transaction.isDeleted && canAccess('Ver transações'),
    },
    {
      id: 'details',
      icon: faEye,
      title: 'Ver Detalhes',
      buttonClass: 'btn-info',
      permission: 'Ver transações',
      onClick: (product) =>
        navigate(`/almoxarifado/transacoes/detalhes/${product.id}`),
      show: (product) => !product.isDeleted && canAccess('Ver transações'),
    },
    {
      id: 'edit',
      icon: faEdit,
      title: 'Editar',
      buttonClass: 'btn-primary',
      permission: 'Atualizar transações',
      onClick: (product) =>
        navigate(`/almoxarifado/transacoes/editar/${product.id}?type=entrada`),
      show: (product) =>
        !product.isDeleted && canAccess('Atualizar transações'),
    },
    {
      id: 'delete',
      icon: faTrash,
      title: 'Excluir',
      buttonClass: 'btn-danger',
      permission: 'Excluir transações',
      onClick: (product) =>
        showConfirmation(
          product,
          `Você tem certeza que deseja excluir?`,
          async () => {
            try {
              await inactivateTransactionMutation.mutateAsync(product.id)
            } catch (error) {
              console.error('Erro ao excluir transação:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (product) => !product.isDeleted && canAccess('Excluir transações'),
    },
    {
      id: 'activate',
      icon: faUndo,
      title: 'Restaurar',
      buttonClass: 'btn-info',
      permission: 'Excluir transações',
      onClick: (product) =>
        showConfirmation(
          product,
          `Você tem certeza que deseja restaurar?`,
          async () => {
            try {
              await activateTransactionMutation.mutateAsync(product.id)
            } catch (error) {
              console.error('Erro ao restaurar transação:', {
                status: error.status,
                message: error.message,
              })
            }
          }
        ),
      show: (product) => product.isDeleted && canAccess('Excluir transações'),
    },
  ]

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Transações" showBackButton backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsFiltersEntrance({
            localFilterValues,
            handleFilterChange,
          })}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Lista de Transações de Entrada"
          buttonText="Cadastrar Transação"
          buttonLink="/almoxarifado/transacoes/criar?type=entrada"
          permission="Criar transações"
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={transactions}
          columns={columns}
          actions={actions}
          collapseConfig={collapseConfig}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="collapse"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem?.name || ''}
          text={action?.text}
        />
      </div>
    </MainLayout>
  )
}

export default EntranceTransactionsPage
